<template>
    <div class="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div class="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-2.5">
            <h3 class="text-sm font-semibold text-slate-800">{{ sectionTitle }}</h3>
            <span class="rounded-md bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 tabular-nums ring-1 ring-slate-200/80">
                {{ data.length }}
            </span>
        </div>

        <div class="p-3.5">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div class="flex min-w-0 flex-1 items-center gap-2">
                    <input
                        v-if="activeTab === 'Panels'"
                        type="text"
                        v-model="width"
                        :placeholder="`${type} width`"
                        class="block w-full rounded-xl border-0 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-1 -outline-offset-1 outline-slate-200 placeholder:text-slate-400 focus:bg-white focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:text-white"
                    />
                    <input
                        v-else
                        type="text"
                        v-model="width"
                        :placeholder="`${type} length`"
                        class="block w-full rounded-xl border-0 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-1 -outline-offset-1 outline-slate-200 placeholder:text-slate-400 focus:bg-white focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:text-white"
                    />
                    <span class="shrink-0 text-xs font-medium text-slate-400">×</span>
                    <input
                        v-if="activeTab === 'Panels'"
                        type="text"
                        v-model="height"
                        :placeholder="`${type} height`"
                        @keydown.enter.prevent="addMeasurement(type)"
                        class="block w-full rounded-xl border-0 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-1 -outline-offset-1 outline-slate-200 placeholder:text-slate-400 focus:bg-white focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:text-white"
                    />
                    <input
                        v-else
                        type="text"
                        v-model="height"
                        :placeholder="`${type} width`"
                        @keydown.enter.prevent="addMeasurement(type)"
                        class="block w-full rounded-xl border-0 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-1 -outline-offset-1 outline-slate-200 placeholder:text-slate-400 focus:bg-white focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:text-white"
                    />
                </div>
                <button
                    type="button"
                    class="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500"
                    @click="addMeasurement(type)"
                >
                    <PlusIcon class="size-4" aria-hidden="true" />
                    Add
                </button>
            </div>

            <p v-if="formErrors.error" class="mt-2 text-xs text-red-600">{{ formErrors.error }}</p>

            <ul v-if="data.length" role="list" class="mt-3 divide-y divide-slate-100 border-t border-slate-100">
                <li v-for="(attribute, index) in data" :key="`${type}-${index}`" class="flex flex-wrap items-center justify-between gap-2 py-2.5">
                    <p class="text-sm font-medium text-slate-700 tabular-nums">
                        <template v-if="activeTab === 'Panels'">
                            {{ helper.formatMeasurement(attribute.width) }} × {{ helper.formatMeasurement(attribute.height) }}
                        </template>
                        <template v-else>
                            {{ helper.formatMeasurement(attribute.length) }} × {{ helper.formatMeasurement(attribute.width) }}
                        </template>
                    </p>
                    <div class="flex items-center gap-3">
                        <button
                            type="button"
                            class="cursor-pointer text-xs font-medium text-indigo-600 transition hover:text-indigo-500"
                            @click="cloneMeasurement(type, index)"
                        >
                            Duplicate
                        </button>
                        <button
                            type="button"
                            class="cursor-pointer text-xs font-medium text-red-600 transition hover:text-red-500"
                            @click="removeMeasurement(type, index)"
                        >
                            Remove
                        </button>
                    </div>
                </li>
            </ul>
            <p v-else class="mt-3 text-xs text-slate-400">No {{ sectionTitle.toLowerCase() }} added yet.</p>
        </div>
    </div>
</template>

<script setup>
import { PlusIcon } from '@heroicons/vue/20/solid';
import { computed, ref } from 'vue';
import { useAppHelpers } from '@/composables/useAppHelpers.js';
import { useCalculatorStore } from '@/stores/calculator-store.js';

const props = defineProps({
    activeTab: null,
    currentRoom: null,
    type: {
        type: String,
        default: 'wall',
    },
    label: {
        type: String,
        default: '',
    },
    data: {
        type: Array,
        default: () => [],
    },
});

const helper = useAppHelpers();
const calcStore = useCalculatorStore();

const width = ref('');
const height = ref('');
const formErrors = ref({});

const sectionTitle = computed(() => {
    if (props.label) return props.label.endsWith('s') ? props.label : `${props.label}s`;
    const map = {
        wall: 'Walls',
        window: 'Windows',
        door: 'Doors',
        vent: 'Vents',
        roof: 'Ceiling sections',
        floor: 'Floor sections',
    };
    return map[props.type] || `${props.type}s`;
});

const addMeasurement = (type) => {
    formErrors.value = {};
    const category = props.activeTab?.toLowerCase();

    if (width.value.trim() !== '' && height.value.trim() !== '' && props.currentRoom?.id && type && category) {
        const widthInches = helper.toInches(width.value);
        if (widthInches === 'Invalid measurement format') {
            formErrors.value.error = 'Invalid format. Use 10, 10\'2", or 24".';
            return;
        }
        const heightInches = helper.toInches(height.value);
        if (heightInches === 'Invalid measurement format') {
            formErrors.value.error = 'Invalid format. Use 10, 10\'2", or 24".';
            return;
        }
        if (widthInches <= 0 || heightInches <= 0) {
            formErrors.value.error = 'Measurements must be greater than zero.';
            return;
        }

        calcStore.addMeasurement(props.currentRoom.id, category, type, width.value, height.value);
        width.value = '';
        height.value = '';
    }
};

const removeMeasurement = (type, index) => {
    const category = props.activeTab?.toLowerCase();
    if (props.currentRoom?.id && type && category) {
        calcStore.removeMeasurement(props.currentRoom.id, category, type, index);
    }
};

const cloneMeasurement = (type, index) => {
    const category = props.activeTab?.toLowerCase();
    if (props.currentRoom?.id && type && category) {
        calcStore.cloneMeasurement(props.currentRoom.id, category, type, index);
    }
};
</script>
